import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy, Users, Sparkles, Plus, Handshake, Award, HeartHandshake, Loader2, MapPin, Calendar,
  ArrowRight, CheckCircle2, TreeDeciduous, Droplet, Leaf, Star,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { ClaimDialog } from "@/components/volunteer/ClaimDialog";
import { ContributionHistory } from "@/components/volunteer/ContributionHistory";
import { ReviewQueue } from "@/components/volunteer/ReviewQueue";
import { PointTotals } from "@/components/volunteer/PointTotals";

const INTERESTS = [
  "education", "health", "environment", "disaster_relief", "employment",
  "digital_help", "blood_donation", "animal_welfare", "elderly_support",
  "child_safety", "women_empowerment", "cleanliness", "infrastructure",
];
const AVAILABILITY = ["daily", "weekly", "monthly", "on_demand"];
const CATEGORIES = [
  "blood_drive", "clean_up", "tree_planting", "digital_literacy",
  "education", "food_distribution", "elderly_visit", "animal_welfare",
  "medical_camp", "skill_training", "disaster_relief", "women_safety",
];

const BADGE_STYLE = {
  "Newcomer":     "bg-stone-100 text-stone-700",
  "Contributor":  "bg-sky-100 text-sky-800",
  "Trusted":      "bg-emerald-100 text-emerald-800",
  "Verified":     "bg-fuchsia-100 text-fuchsia-800",
  "City Builder": "bg-gradient-to-br from-amber-100 to-yellow-200 text-amber-900",
};

// Quick contributions create CLAIMS with evidence. Points count only after independent
// verification (invites are verified automatically once the friend signs up and verifies).
const QUICK_CONTRIB = [
  { key: "add_business",     label: "Added a business",      points: 25, icon: Plus },
  { key: "verify_business",  label: "Verified a listing",    points: 10, icon: CheckCircle2 },
  { key: "fix_info",         label: "Fixed wrong info",      points: 8,  icon: Sparkles },
  { key: "add_photo",        label: "Added a photo",         points: 5,  icon: Star },
  { key: "invite_friend",    label: "Invited a friend",      points: 15, icon: Users },
  { key: "report_fake",      label: "Reported a fake",       points: 12, icon: Award },
  { key: "help_elderly",     label: "Helped an elderly user",points: 30, icon: HeartHandshake },
  { key: "digital_help",     label: "Digital literacy help", points: 20, icon: Sparkles },
];

export default function Volunteer() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [projects, setProjects] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [pForm, setPForm] = useState({ title: "", description: "", category: "tree_planting", city: "", starts_on: "", volunteers_needed: 5 });
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [claimKind, setClaimKind] = useState(null);
  const [mine, setMine] = useState([]);
  const [elig, setElig] = useState(null);

  useSeo({
    title: "Volunteer & City Builder · find2near",
    description: "Join community projects, log contributions and earn reputation badges.",
    canonical: "/volunteer",
  });

  const refresh = async () => {
    const [pr, lb] = await Promise.all([
      api.get("/civic/projects").catch(() => ({ data: [] })),
      api.get("/civic/leaderboard").catch(() => ({ data: [] })),
    ]);
    setProjects(pr.data);
    setLeaderboard(lb.data);
    if (user) {
      try { setProfile((await api.get("/civic/me")).data); } catch {}
      api.get("/civic/contributions").then((r) => setMine(r.data)).catch(() => setMine([]));
      api.get("/civic/review/eligibility").then((r) => setElig(r.data)).catch(() => setElig(null));
    }
  };

  useEffect(() => { refresh(); }, [user]);

  const toggleInterest = async (i) => {
    if (!profile) return;
    const has = profile.interests.includes(i);
    const next = has ? profile.interests.filter(x => x !== i) : [...profile.interests, i];
    try {
      const r = await api.put("/civic/me", { interests: next });
      setProfile(r.data);
    } catch (e) { toast.error("Update failed"); }
  };

  const setAvailability = async (a) => {
    try {
      const r = await api.put("/civic/me", { availability: a });
      setProfile(r.data);
    } catch (e) { toast.error("Update failed"); }
  };

  const logContribution = async (kind) => {
    if (!user) { toast.error("Sign in to contribute"); return; }
    if (kind !== "invite_friend") { setClaimKind(kind); return; }
    try {
      const r = await api.post("/civic/invites/sync");
      const n = r.data.new?.approved || 0;
      toast.success(n ? `${n} verified invite(s) rewarded` : "Invite rewards are automatic once a friend signs up with your code and verifies");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const confirmParticipation = async (id) => {
    try {
      const r = await api.post(`/civic/projects/${id}/participation`, { reason: "Took part" });
      toast.success(r.data.message);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const activeClaim = QUICK_CONTRIB.find((q) => q.key === claimKind);

  const createProject = async () => {
    if (!user) { toast.error("Sign in to create a project"); return; }
    if (!pForm.title.trim() || !pForm.description.trim()) { toast.error("Title + description required"); return; }
    try {
      await api.post("/civic/projects", pForm);
      toast.success("Project created — invite volunteers to join");
      setPForm({ title: "", description: "", category: "tree_planting", city: pForm.city, starts_on: "", volunteers_needed: 5 });
      setShowProjectForm(false);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  const joinProject = async (id) => {
    if (!user) { toast.error("Sign in to join"); return; }
    try {
      const r = await api.post(`/civic/projects/${id}/join`);
      toast.success(r.data.already_member ? "You're already a member" : "Joined — confirm your participation after the event to earn points");
      refresh();
    } catch (e) { toast.error("Join failed"); }
  };

  const nextBadgeInfo = useMemo(() => {
    if (!profile) return null;
    const tiers = [
      ["Newcomer", 0], ["Contributor", 50], ["Trusted", 200], ["Verified", 500], ["City Builder", 1500],
    ];
    for (const [name, threshold] of tiers) {
      if ((profile.approved_points || 0) < threshold) return { name, threshold, remaining: threshold - (profile.approved_points || 0) };
    }
    return null;
  }, [profile]);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" data-testid="volunteer-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2">
          <Handshake size={12} /> City Builder
        </div>
        <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">
          Build your city, <span className="text-[#E05E36]">one act at a time.</span>
        </h1>
        <p className="text-stone-500 text-sm mt-1 max-w-xl">
          Volunteer for causes, join community projects and earn reputation badges that boost your visibility across find2near.
        </p>
      </div>

      {/* Profile card */}
      {user && profile && (
        <div className="rounded-3xl bg-gradient-to-br from-[#2D5A27] to-emerald-800 text-white p-5 md:p-6 mb-6 relative overflow-hidden" data-testid="vol-profile">
          <div className="absolute -right-6 -bottom-6 w-40 h-40 rounded-full bg-white/15 blur-3xl" />
          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 font-black">Volunteer profile</div>
              <div className="heading text-3xl font-black">{profile.name}</div>
              <div className="flex items-center gap-3 text-xs text-white/80 mt-1">
                <span className="tabular-nums font-black" data-testid="vol-approved-points">{profile.approved_points || 0} verified pts</span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black ${BADGE_STYLE[profile.badge] || BADGE_STYLE["Newcomer"]}`}>
                  <Award size={11} /> {profile.badge}
                </span>
                <span>{profile.projects_joined} projects · {profile.approved_contributions || 0} verified contributions</span>
              </div>
              {nextBadgeInfo && (
                <div className="text-[11px] text-white/60 mt-1">
                  {nextBadgeInfo.remaining} more verified points → <b className="text-[#DDA77B]">{nextBadgeInfo.name}</b>
                </div>
              )}
              {profile.legacy_notice && (
                <div className="text-[11px] mt-2 bg-violet-500/30 rounded-lg px-2 py-1 max-w-md" data-testid="vol-legacy-notice">{profile.legacy_notice}</div>
              )}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/60 font-black">Availability</div>
              <div className="flex flex-wrap gap-1 mt-1 justify-end">
                {AVAILABILITY.map((a) => (
                  <button
                    key={a}
                    onClick={() => setAvailability(a)}
                    className={`text-[10px] font-bold px-2 py-1 rounded-full transition-colors ${profile.availability === a ? "bg-white text-[#2D5A27]" : "bg-white/10 text-white/80 hover:bg-white/20"}`}
                    data-testid={`vol-avail-${a}`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="relative"><PointTotals profile={profile} /></div>
          <div className="mt-4 relative">
            <div className="text-[10px] uppercase tracking-widest text-white/60 font-black mb-1">Interests</div>
            <div className="flex flex-wrap gap-1.5">
              {INTERESTS.map((i) => {
                const active = profile.interests.includes(i);
                return (
                  <button
                    key={i}
                    onClick={() => toggleInterest(i)}
                    className={`text-[11px] font-bold px-3 py-1 rounded-full transition-colors ${active ? "bg-[#E05E36] text-white" : "bg-white/10 text-white/80 hover:bg-white/20"}`}
                    data-testid={`vol-interest-${i}`}
                  >
                    {i.replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!user && (
        <div className="rounded-3xl bg-white border border-stone-200 p-5 text-center mb-6">
          <Sparkles className="mx-auto text-[#E05E36]" size={32} />
          <p className="mt-2 text-stone-700">Sign in to register as a volunteer and start earning badges.</p>
          <Link to="/onboarding" className="mt-3 inline-block bg-[#E05E36] text-white px-5 py-2 rounded-full font-bold" data-testid="vol-signin">Get started</Link>
        </div>
      )}

      {/* Quick contributions */}
      {user && (
        <div className="rounded-3xl bg-white border border-stone-200 p-5 mb-6" data-testid="vol-quick-contrib">
          <div className="flex items-center justify-between mb-3">
            <h2 className="heading text-lg font-black text-stone-900 flex items-center gap-2">
              <Sparkles size={16} className="text-[#E05E36]" /> Quick contributions
            </h2>
            <span className="text-[10px] text-stone-400 font-bold">Submit with evidence → points after verification</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {QUICK_CONTRIB.map((q) => (
              <button
                key={q.key}
                onClick={() => logContribution(q.key)}
                className="rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 p-3 text-left transition-colors"
                data-testid={`vol-contrib-${q.key}`}
              >
                <div className="flex items-center justify-between">
                  <q.icon size={14} className="text-emerald-700" />
                  <span className="text-[10px] font-black text-emerald-700">+{q.points} on verify</span>
                </div>
                <div className="text-sm font-bold text-stone-900 mt-1">{q.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {user && <ContributionHistory items={mine} onChange={refresh} />}
      {user && elig?.can_review && <ReviewQueue isAdmin={elig.admin} />}
      <ClaimDialog kind={claimKind} label={activeClaim?.label} points={activeClaim?.points}
        onClose={() => setClaimKind(null)} onDone={() => { setClaimKind(null); refresh(); }} />

      {/* Projects */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="heading text-lg font-black text-stone-900 flex items-center gap-2">
            <Users size={16} className="text-[#2D5A27]" /> Community projects
          </h2>
          {user && (
            <button
              onClick={() => setShowProjectForm(!showProjectForm)}
              className="text-xs font-black px-3 py-1.5 rounded-full bg-[#2D5A27] hover:bg-emerald-800 text-white inline-flex items-center gap-1"
              data-testid="vol-project-toggle"
            >
              <Plus size={12} /> {showProjectForm ? "Close" : "New project"}
            </button>
          )}
        </div>

        {showProjectForm && (
          <div className="rounded-3xl bg-white border border-stone-200 p-4 mb-3" data-testid="vol-project-form">
            <div className="grid gap-2 md:grid-cols-2">
              <input value={pForm.title} onChange={(e) => setPForm({ ...pForm, title: e.target.value })} placeholder="Project title" className="px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="proj-title" />
              <select value={pForm.category} onChange={(e) => setPForm({ ...pForm, category: e.target.value })} className="px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="proj-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
              </select>
              <input value={pForm.city} onChange={(e) => setPForm({ ...pForm, city: e.target.value })} placeholder="City" className="px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="proj-city" />
              <input type="date" value={pForm.starts_on} onChange={(e) => setPForm({ ...pForm, starts_on: e.target.value })} className="px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="proj-starts-on" />
            </div>
            <textarea
              value={pForm.description}
              onChange={(e) => setPForm({ ...pForm, description: e.target.value })}
              placeholder="What are we doing? When? What do volunteers bring?"
              rows={3}
              className="mt-2 w-full px-4 py-2 rounded-2xl border border-stone-200 text-sm outline-none"
              data-testid="proj-description"
            />
            <button
              onClick={createProject}
              className="mt-2 bg-[#2D5A27] hover:bg-emerald-800 text-white font-black px-5 py-2 rounded-full inline-flex items-center gap-2"
              data-testid="proj-create"
            >
              <Plus size={14} /> Create project
            </button>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3" data-testid="vol-projects-list">
          {projects.length === 0 ? (
            <div className="col-span-full text-center text-sm text-stone-400 py-10 bg-white border border-stone-200 rounded-2xl">
              No projects yet. Be the first — start one.
            </div>
          ) : (
            projects.map((p) => (
              <div key={p.project_id} className="rounded-2xl bg-white border border-stone-200 p-4" data-testid={`proj-${p.project_id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#E05E36]">{p.category.replace(/_/g, " ")}</div>
                  <span className="text-[10px] text-stone-400">{p.member_count}/{p.volunteers_needed} joined</span>
                </div>
                <h3 className="heading text-base font-black text-stone-900 mt-1">{p.title}</h3>
                <p className="text-sm text-stone-600 mt-1 line-clamp-2">{p.description}</p>
                <div className="text-[11px] text-stone-500 mt-2 flex items-center gap-2 flex-wrap">
                  {p.city && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {p.city}</span>}
                  {p.starts_on && <span className="inline-flex items-center gap-1"><Calendar size={10} /> {p.starts_on}</span>}
                  <span>· by {p.owner_name}</span>
                </div>
                {user && (p.members || []).includes(user.user_id) && p.owner_id !== user.user_id ? (
                <button
                  onClick={() => confirmParticipation(p.project_id)}
                  className="mt-3 w-full bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 font-black py-2 rounded-full text-sm"
                  data-testid={`proj-participate-${p.project_id}`}
                >
                  I took part · ask organiser to confirm
                </button>
                ) : (
                <button
                  onClick={() => joinProject(p.project_id)}
                  className="mt-3 w-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 font-black py-2 rounded-full text-sm inline-flex items-center justify-center gap-1"
                  data-testid={`proj-join-${p.project_id}`}
                >
                  Join <ArrowRight size={13} />
                </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Leaderboard */}
      <div className="rounded-3xl bg-white border border-stone-200 p-5" data-testid="vol-leaderboard">
        <div className="flex items-center gap-2 mb-3">
          <Trophy size={18} className="text-amber-500" />
          <h2 className="heading text-lg font-black text-stone-900">Top City Builders</h2>
        </div>
        {leaderboard.length === 0 ? (
          <div className="text-sm text-stone-400 py-6 text-center">The leaderboard fills up as citizens contribute.</div>
        ) : (
          <ol className="grid md:grid-cols-2 gap-2">
            {leaderboard.map((r, i) => (
              <li key={r.user_id + i} className="flex items-center gap-3 rounded-xl bg-stone-50 px-3 py-2" data-testid={`lb-${i}`}>
                <span className="text-lg font-black text-stone-400 w-8 text-center tabular-nums">#{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-900 truncate">{r.name}</div>
                  <div className="text-[11px] text-stone-500 truncate">{r.city || "—"}</div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black ${BADGE_STYLE[r.badge] || BADGE_STYLE["Newcomer"]}`}>
                  {r.badge}
                </span>
                <span className="text-sm font-black text-stone-800 tabular-nums">{r.points}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
